import { memo } from "react";
import type { SideId } from "../state/types";
import { Pdf } from "../components/Pdf";

/** Ett tidligere dokument i lista under. */
export interface TidligereDokument {
  readonly serieId: string;
  readonly opptaksdag: string;
  /** Datoen dagen faller paa, eller tom. Merket i lista. */
  readonly dato: string;
  readonly versjon: number;
  readonly url: string | null;
  /** Ikke aapnet paa denne telefonen. Se state/dokumenter.ts. */
  readonly ulest: boolean;
}

export interface DocumentState {
  readonly page: SideId;
  readonly title: string;
  /** «v4 · lastet 05:40», eller null når ingenting er delt. */
  readonly meta: string | null;
  /** Den nedlastede kopien er ikke lenger gjeldende versjon. */
  readonly stale: boolean;
  readonly localUrl: string | null;
  /**
   * De andre opptaksdagene, hoeyeste dag foerst. Én versjon per dag.
   *
   * Brukes naar timene dobbeltsjekkes i ettertid: «hva sto det paa dagsplanen
   * den 14.?» Alt forsvinner paa produksjonens sluttdato, sammen med resten.
   *
   * IKKE BARE TIDLIGERE, selv om feltet heter det. Lista er alle dager som
   * ikke er dagens, og en kameraplan publiseres gjerne FRAM I TID — A-foto
   * tegner dag 9 mens crewet staar paa dag 7. Derfor staar det «Andre
   * opptaksdager» i flata.
   */
  readonly tidligere: readonly TidligereDokument[];
  /** Dagens dokument er ikke aapnet paa denne telefonen. */
  readonly ulest: boolean;
  /** Serien dagens dokument hoerer til. Merket toemmes paa den. */
  readonly serieId: string | null;
}

/**
 * Dagsmanus, dagsplan og kjøreplan.
 *
 * Dokumentet forlater aldri appen: innebygd visning, ingen delingsknapp,
 * ingen «åpne i …». Det er forutsetningen for at den lokale kopien faktisk
 * kan slettes. Se docs/12-ui-feltapp.md.
 *
 * Trykk maksimerer flaten. I liten tilstand er det ingen pinch, bare trykk;
 * i maksimert tilstand er det ingen sidewipe. Da finnes gestekonflikten ikke.
 *
 * BLAR MAN NED, kommer tidligere opptaksdager. De ligger under dagens
 * dokument og ikke i en egen fane, fordi det er samme sak — bare en annen
 * dag. Rullingen er VERTIKAL, og den kolliderer ikke med sidebytet, som er
 * horisontalt.
 */
/* Memo: klokka gjengir skallet ti ganger i sekundet, og uten dette
   bygges hele denne flata paa nytt hver gang — inkludert ref-ene paa
   hvert kort, som React da kobler fra og paa igjen. */
/** «2. nov». Kort nok til aa staa ved siden av dagnummeret. */
function datoKort(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("nb-NO", { day: "numeric", month: "short" });
}

export const DocumentScreen = memo(function DocumentScreen({
  doc, page, onOpen, onOpenTidligere,
}: {
  doc: DocumentState;
  /* Sida foelger med som eget felt, saa handleren kan vaere stabil. En ny
     pil per render ville gjort memo uvirksom. */
  page: SideId;
  onOpen(page: SideId): void;
  onOpenTidligere(t: TidligereDokument): void;
}): JSX.Element {
  const missing = doc.meta === null;

  return (
    /* Rullingen ligger her og ikke paa `.doc`: hodet skal foelge med opp, slik
       at man ser hvilket dokument lista hoerer til mens man blar. */
    <div className="surface surface--rull" style={{ height: "100%" }}>
      <div className="doc">
        <div className="doc__head">
          <span className="doc__name">
            {doc.title}
            {/* ULEST AV DEG. Ikke en egenskap ved dokumentet — femten i
                crewet leser den samme dagsplanen, og hver av dem har
                sitt eget svar. En NY versjon er ulest igjen. Se
                state/dokumenter.ts. */}
            {!doc.ulest ? null : (
              <span className="doc__ulest" aria-label="ulest" />
            )}
          </span>
          <span className="doc__meta">{doc.meta ?? "ikke delt"}</span>
        </div>

        {/* Advarselen er synlig i liten tilstand — man skal ikke måtte
            maksimere for å oppdage at kopien er utdatert. */}
        {doc.stale ? (
          <div className="doc__stale">
            <i />
            <span>Ny versjon finnes. Kopien er fra i går.</span>
          </div>
        ) : null}

        {missing ? (
          // Ikke en blank flate — blankt ser ut som en feil, og folk ringer.
          <div className="doc__missing">
            <span style={{ fontWeight: 500, fontSize: 15, color: "var(--ink)" }}>
              Dokument mangler
            </span>
            <span className="mono" style={{ fontSize: 12.5, lineHeight: 1.6, color: "var(--ink-dim)" }}>
              Ingen {doc.title.toLowerCase()} er delt med deg for denne opptaksdagen.
            </span>
          </div>
        ) : (
          <>
            {/* Dokumentet vises OGSAA her, ikke bare i full skjerm. Et svart
                felt med «Trykk for aa lese» sier ikke om det er riktig
                dokument — og det er nettopp det man sjekker i et blikk.
                Bare foerste side: dette er et oppslag, ikke en leser. */}
            <button
              type="button"
              className="doc__page"
              onClick={() => onOpen(page)}
              aria-label={`Åpne ${doc.title}`}
            >
              <Pdf url={doc.localUrl} sider={1} />
            </button>
            <span className="doc__hint">Trykk for å lese</span>
          </>
        )}

        {doc.tidligere.length === 0 ? null : (
          <div className="doc__tidligere">
            <span className="doc__tidligere-tittel">Andre opptaksdager</span>
            {doc.tidligere.map((t) => (
              <button
                key={t.serieId}
                type="button"
                className="doc__tidligere-rad"
                disabled={t.url === null}
                onClick={() => onOpenTidligere(t)}
              >
                <span className="doc__tidligere-dag">
                  {/* DATOEN, ikke bare dagnummeret. «Opptaksdag 9» sier
                      ingenting om naar det var — brukeren 02.11.2026:
                      «med et merke om at denne er til den datoen». */}
                  Opptaksdag {t.opptaksdag}
                  {t.dato === "" ? "" : ` · ${datoKort(t.dato)}`}
                  {!t.ulest ? null : (
                    <span className="doc__ulest" aria-label="ulest" />
                  )}
                </span>
                <span className="doc__tidligere-meta mono">
                  {t.url === null ? "ikke lastet ned" : `v${t.versjon}`}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});
